import cx from 'classnames';
import { rest } from 'lodash';
import { forwardRef } from 'react';
import { Form, FormControl, FormGroup } from 'react-bootstrap';
import { useController } from 'react-hook-form';
import { icons } from '../../assets';
import Loader from '../Loader/Loader';
import styles from './InputController.module.scss';

export const InputController = ({
  control,
  name,
  label,
  className,
  multiline,
  noResize,
  style,
  height,
  ...rest
}) => {
  const {
    field: { onChange, onBlur, value, ref },
    fieldState: { invalid, isTouched, isDirty, error },
    formState: { touchedFields, dirtyFields },
  } = useController({
    name,
    control,
  });

  const hasError = Boolean(error?.message);

  return (
    <InputUI
      hasError={hasError}
      label={label}
      value={value}
      name={name}
      onChange={onChange}
      onBlur={onBlur}
      multiline={multiline}
      style={style}
      error={error}
      className={className}
      noResize={noResize}
      height={height}
      ref={ref}
      {...rest}
    />
  );
};

export const InputUI = forwardRef(
  (
    {
      hasError,
      label,
      value,
      name,
      onChange,
      onBlur,
      multiline,
      style,
      error,
      className,
      noResize,
      height,
      inputClassName,
      variant,
      labelClassName,
      as,
      theme,
      inline,
      loading,
      size,
      addDragCursor,
      ...rest
    },
    ref
  ) => {
    const isV2 = variant === 'v2';

    const inputComponent = as || (multiline ? 'textarea' : 'input');

    const input = (
      <FormControl
        {...rest}
        value={value ?? ''}
        name={name}
        onChange={onChange}
        onBlur={onBlur}
        ref={ref}
        as={inputComponent}
        className={cx(styles.input, inputClassName, loading && styles.inputLoading, addDragCursor && styles.hoverDrag)}
        style={{
          ...style,
          resize: noResize ? 'none' : 'auto',
          height,
        }}
      />
    );

    const hasLoader = typeof loading === 'boolean';

    return (
      <FormGroup
        className={cx(
          className,
          hasError && styles.error,
          inline && styles.inline,
          theme && styles[`${theme}Theme`],
          size && styles[`${size}Size`],
        )}>
        {label && (
          <Form.Label
            className={cx(
              styles.label,
              hasError && 'text-danger',
              isV2 && styles.v2Label,
              labelClassName
            )}>
            {hasError && isV2 && <icons.ExclamationIcon className={styles.errorIcon} />}
            {hasError && isV2 ? error?.message : label}
          </Form.Label>
        )}

        {hasLoader ? (
          <div className={styles.inputContainer}>
            {input}
            {loading && <Loader loading thickness={2} size={20} className={styles.loader} />}
          </div>
        ) : (
          input
        )}

        {!isV2 && (
          <FormControl.Feedback
            type={hasError ? 'invalid' : 'valid'}
            style={{
              display: hasError ? 'block' : 'none', // TODO: work on it, for some reason bootstrap doesn't show invalid feedback
            }}>
            {error?.message}
          </FormControl.Feedback>
        )}
      </FormGroup>
    );
  }
);
